import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Download, RefreshCcw } from "lucide-react";
import { PageHeader, Pill, SectionCard } from "../../components/Ui";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { api, extractError } from "../../lib/api";
import { downloadApiFile } from "../../lib/downloadApiFile";
import { formatListDate } from "../../lib/purchaseListMeta";
import { toast } from "sonner";
import {
  MajorKpiGrid,
  VerdictPill,
  formatCurrency,
  formatPartyKind,
  formatPct,
  formatQty,
} from "./purchaseReportShared";

const PAGE_SIZE = 50;
const DEFAULT_FILTERS = {
  from_date: "",
  to_date: "",
  variety: "",
  supplier: "",
  broker: "",
  warehouse: "",
  party_kind: "",
};

export default function PurchaseReportsPage() {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);
  const [tab, setTab] = useState("item");
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState(null);

  const params = useMemo(
    () => ({
      page,
      page_size: PAGE_SIZE,
      from_date: filters.from_date || undefined,
      to_date: filters.to_date || undefined,
      variety: filters.variety || undefined,
      supplier: filters.supplier || undefined,
      broker: filters.broker || undefined,
      warehouse: filters.warehouse || undefined,
      party_kind: filters.party_kind || undefined,
    }),
    [filters, page],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/purchase-reports/dashboard", { params });
      setPayload(data);
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [filters.from_date, filters.to_date, filters.variety, filters.supplier, filters.broker, filters.warehouse, filters.party_kind]);

  const handleExport = async (mode) => {
    try {
      if (mode === "excel") {
        await downloadApiFile("/purchase-reports/export/excel", `purchase-${tab}-${Date.now()}.csv`, { ...params, view: tab });
        toast.success("Excel export started");
        return;
      }
      if (mode === "pdf") {
        await downloadApiFile("/purchase-reports/export/pdf", `purchase-${tab}-${Date.now()}.pdf`, { ...params, view: tab });
        toast.success("PDF export started");
      }
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const voucherMeta = payload?.voucher_log || { count: 0, page: 1, page_size: PAGE_SIZE, results: [] };
  const totalPages = Math.max(1, Math.ceil((voucherMeta.count || 0) / PAGE_SIZE));

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="purchase-reports-page">
      <PageHeader
        eyebrow="Purchase Reports"
        title="Master Purchase Reporting Dashboard"
        description="High-volume procurement reporting across item, party, voucher, and contract views with landed-cost visibility."
        actions={
          <>
            <Button type="button" variant="outline" onClick={load} disabled={loading}>
              <RefreshCcw className="h-4 w-4 mr-2" />
              {loading ? "Refreshing..." : "Refresh"}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" className="bg-bansal-sage hover:bg-bansal-sageDark text-white">
                  <Download className="h-4 w-4 mr-2" />
                  Export
                  <ChevronDown className="h-4 w-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleExport("excel")}>Export Excel</DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport("pdf")}>Export PDF</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        }
      />


      <SectionCard title="Global Filters">
        <div className="flex flex-wrap items-center gap-2">
          <Input
            type="date"
            title="From date"
            aria-label="From date"
            value={filters.from_date}
            onChange={(e) => setFilters((f) => ({ ...f, from_date: e.target.value }))}
            className="h-8 w-[9.5rem] shrink-0 px-2 text-sm"
          />
          <Input
            type="date"
            title="To date"
            aria-label="To date"
            value={filters.to_date}
            onChange={(e) => setFilters((f) => ({ ...f, to_date: e.target.value }))}
            className="h-8 w-[9.5rem] shrink-0 px-2 text-sm"
          />
          <Input
            placeholder="Item / variety"
            aria-label="Item / variety"
            value={filters.variety}
            onChange={(e) => setFilters((f) => ({ ...f, variety: e.target.value }))}
            className="h-8 w-36 sm:w-40 shrink-0 px-2 text-sm"
          />
          <Input
            placeholder="Supplier / vendor"
            aria-label="Supplier / vendor"
            value={filters.supplier}
            onChange={(e) => setFilters((f) => ({ ...f, supplier: e.target.value }))}
            className="h-8 w-36 sm:w-40 shrink-0 px-2 text-sm"
          />
          <Input
            placeholder="Broker"
            aria-label="Broker"
            value={filters.broker}
            onChange={(e) => setFilters((f) => ({ ...f, broker: e.target.value }))}
            className="h-8 w-32 sm:w-36 shrink-0 px-2 text-sm"
          />
          <Input
            placeholder="Godown / warehouse"
            aria-label="Godown / warehouse"
            value={filters.warehouse}
            onChange={(e) => setFilters((f) => ({ ...f, warehouse: e.target.value }))}
            className="h-8 w-36 sm:w-40 shrink-0 px-2 text-sm"
          />
          <div className="flex flex-wrap items-center gap-1.5">
            {[
              { value: "", label: "All" },
              { value: "SUPPLIER", label: "Supplier" },
              { value: "FARMER", label: "Farmer" },
            ].map((option) => (
              <Button
                key={option.label}
                type="button"
                size="sm"
                variant={filters.party_kind === option.value ? "default" : "outline"}
                className={`h-8 px-2.5 text-xs ${filters.party_kind === option.value ? "bg-bansal-sage hover:bg-bansal-sageDark text-white" : ""}`}
                onClick={() => setFilters((f) => ({ ...f, party_kind: option.value }))}
              >
                {option.label}
              </Button>
            ))}
            <Button type="button" size="sm" variant="outline" className="h-8 px-2.5 text-xs" onClick={() => setFilters(DEFAULT_FILTERS)}>
              Clear
            </Button>
          </div>
        </div>
      </SectionCard>

      <MajorKpiGrid
        items={[
          { label: "Total intake", value: `${formatQty(payload?.kpis?.intake_qtl)} qtl`, accent: true },
          { label: "Basic purchase value", value: formatCurrency(payload?.kpis?.basic_value) },
          { label: "Weighted avg rate", value: formatCurrency(payload?.kpis?.weighted_avg_rate), sub: "Received · per qtl" },
          { label: "Contracted weighted avg", value: formatCurrency(payload?.kpis?.contracted_weighted_avg_rate), sub: "Contract · per qtl" },
          { label: "Total landed cost", value: formatCurrency(payload?.kpis?.landed_cost) },
          { label: "Rejected quantity", value: `${formatQty(payload?.kpis?.rejected_qtl)} qtl`, sub: formatPct(payload?.kpis?.rejected_pct) },
          { label: "Outstanding payables", value: formatCurrency(payload?.kpis?.outstanding_payables), accent: true },
          { label: "Active contracts", value: payload?.kpis?.active_contracts ?? 0 },
        ]}
      />

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className="bg-card border border-border p-1 h-auto flex-wrap">
          <TabsTrigger value="item">Item-Wise Summary</TabsTrigger>
          <TabsTrigger value="party">Party-Wise Summary</TabsTrigger>
          <TabsTrigger value="voucher">Voucher-Wise Log</TabsTrigger>
          <TabsTrigger value="contract">Purchase Contract Details</TabsTrigger>
        </TabsList>

        <TabsContent value="item">
          <SectionCard title="Item-Wise Summary Table">
            <SimpleTable
              columns={["Variety", "Intake", "Rejected", "Basic value", "Weighted avg rate", "Landed cost", "Landed / qtl"]}
              rows={(payload?.item_summary || []).map((row) => [
                row.variety,
                `${formatQty(row.total_intake_qtl)} qtl`,
                `${formatQty(row.rejected_qtl)} qtl`,
                formatCurrency(row.basic_value),
                formatCurrency(row.weighted_avg_rate),
                formatCurrency(row.total_landed_cost),
                formatCurrency(row.landed_cost_per_qtl),
              ])}
              emptyMessage={loading ? "Loading item summary..." : "No item summary rows match these filters."}
            />
          </SectionCard>
        </TabsContent>

        <TabsContent value="party">
          <SectionCard title="Party-Wise Summary Table" subtitle="Click any party to open the detailed operational and financial profile.">
            <SimpleTable
              columns={["Party", "Kind", "Contracts", "Contracted qty", "Net qty received", "Fulfillment", "Gross purchase", "Party brokerage", "Net outstanding", "Received avg", "Contracted avg"]}
              rows={(payload?.party_summary || []).map((row) => [
                <Link key={row.party_id} to={`/purchase/reports/party/${row.party_id}`} className="font-semibold link-brand">
                  {row.party_name}
                </Link>,
                <Pill key={`kind-${row.party_id}`} tone={row.party_kind === "SUPPLIER" ? "sage" : "gold"}>{formatPartyKind(row.party_kind)}</Pill>,
                row.total_contracts,
                `${formatQty(row.contracted_qty)} qtl`,
                `${formatQty(row.net_qty_received)} qtl`,
                formatPct(row.fulfillment_pct),
                formatCurrency(row.gross_purchase_value),
                formatCurrency(row.party_side_brokerage_deductions),
                formatCurrency(row.net_outstanding_payables ?? row.outstanding_payables),
                formatCurrency(row.weighted_avg_rate),
                formatCurrency(row.contracted_weighted_avg_rate),
              ])}
              emptyMessage={loading ? "Loading party summary..." : "No parties match these filters."}
            />
          </SectionCard>
        </TabsContent>

        <TabsContent value="voucher">
          <SectionCard title="Voucher-Wise Log" subtitle="Daily transactional feed combining weighbridge, quality, and payable figures.">
            <SimpleTable
              columns={["Date", "Voucher", "Purchase", "Party", "Vehicle", "Gross", "Tare", "Net", "Moisture", "Broken", "Admixture", "Gross value", "Our brokerage", "Party brokerage", "Net amount", "Landed cost"]}
              rows={(voucherMeta.results || []).map((row) => [
                formatListDate(row.date),
                row.voucher_code,
                row.purchase_id ? <Link key={`p-${row.id}`} to={`/purchases/${row.purchase_id}`} className="link-brand">{row.purchase_code}</Link> : row.purchase_code,
                <div key={`party-${row.id}`} className="min-w-[180px]">
                  <div className="font-medium">{row.party_name}</div>
                  <div className="text-xs text-muted-foreground">{row.warehouse || "—"}</div>
                </div>,
                row.vehicle_number || "—",
                formatQty(row.gross_qtl),
                formatQty(row.tare_qtl),
                formatQty(row.net_qtl),
                row.quality_moisture_pct != null ? formatPct(row.quality_moisture_pct) : "—",
                row.quality_broken_pct != null ? formatPct(row.quality_broken_pct) : "—",
                row.quality_admixture_pct != null ? formatPct(row.quality_admixture_pct) : "—",
                formatCurrency(row.gross_amount),
                formatCurrency(row.our_side_brokerage),
                formatCurrency((row.party_side_brokerage_deduct || 0) + (row.party_side_brokerage_audit || 0)),
                formatCurrency(row.net_amount),
                formatCurrency(row.true_landed_cost),
              ])}
              emptyMessage={loading ? "Loading voucher log..." : "No vouchers match these filters."}
            />
            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Showing page {voucherMeta.page || 1} of {totalPages} · {voucherMeta.count || 0} material voucher(s)
              </p>
              <div className="flex gap-2">
                <Button type="button" variant="outline" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                  Previous
                </Button>
                <Button type="button" variant="outline" disabled={page >= totalPages || loading} onClick={() => setPage((p) => p + 1)}>
                  Next
                </Button>
              </div>
            </div>
          </SectionCard>
        </TabsContent>

        <TabsContent value="contract">
          <SectionCard title="Purchase Contract Details" subtitle="Live delivery vs pending balance with commercial alerts and delivery progress.">
            <div className="space-y-3">
              {(payload?.contract_details || []).length ? (
                payload.contract_details.map((row) => (
                  <div key={row.id} className="rounded-xl border border-border/70 p-4">
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="text-base font-semibold text-foreground">{row.contract_number}</div>
                          <Pill tone={row.stale_alert ? "warning" : "sage"}>{row.status}</Pill>
                          {row.stale_alert ? <Pill tone="danger">Open past contract date</Pill> : null}
                        </div>
                        <div className="text-sm text-muted-foreground mt-1">
                          {row.party_name} · {row.quality || "—"} · {row.broker_name || "No broker"}
                        </div>
                        {row.broker_name ? (
                          <div className="text-xs text-muted-foreground mt-1">
                            Our-side: {row.our_side_brokerage_label || "—"} · Party-side: {row.party_side_brokerage_label || "—"}
                          </div>
                        ) : null}
                        <div className="text-xs text-muted-foreground mt-1">
                          Date: {formatListDate(row.contract_date)} · Delivery: {row.delivery_condition || "—"} · Rate: {formatCurrency(row.agreed_rate)}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 min-w-[320px]">
                        <MiniMetric label="Contracted" value={`${formatQty(row.quantity_qtl)} qtl`} />
                        <MiniMetric label="Received" value={`${formatQty(row.received_qtl)} qtl`} />
                        <MiniMetric label="Pending" value={`${formatQty(row.pending_qtl)} qtl`} />
                        <MiniMetric label="Progress" value={formatPct(row.progress_pct)} />
                      </div>
                    </div>
                    <div className="mt-4">
                      <div className="h-3 rounded-full bg-muted/80 overflow-hidden">
                        <div className="h-full bg-bansal-sage" style={{ width: `${Math.max(0, Math.min(100, row.progress_pct || 0))}%` }} />
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <EmptyText text={loading ? "Loading contract details..." : "No contracts match these filters."} />
              )}
            </div>
          </SectionCard>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function MiniMetric({ label, value }) {
  return (
    <div className="rounded-lg border border-border/70 px-3 py-2 bg-muted/20">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-sm font-semibold text-foreground mt-1">{value}</div>
    </div>
  );
}

function EmptyText({ text }) {
  return <div className="py-12 text-center text-sm text-muted-foreground">{text}</div>;
}

function SimpleTable({ columns, rows, emptyMessage }) {
  if (!rows.length) {
    return <EmptyText text={emptyMessage} />;
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-border/70">
      <table className="w-full text-sm min-w-[920px]">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-[0.08em] font-medium text-muted-foreground bg-muted border-b border-border">
            {columns.map((column) => (
              <th key={column} className="px-3 py-2">{column}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index} className="border-b border-border/60 align-top">
              {row.map((cell, cellIndex) => (
                <td key={cellIndex} className="px-3 py-2 whitespace-nowrap">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
